import { NetworkStatus } from '@apollo/client';
import {
  RestaurantListData,
  salesPortalRestaurantRowTransformer,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/sales-portal-restaurant-transformer';
import { SalesPortalRestaurantListQuery } from '@/types';
import { useCallback, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
  useGeneratedSalesPortalRestaurantListLazyQuery,
  useGeneratedSalesPortalRestaurantListQuery,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/documents/sales-portal-restaurant-list.generated';

type SalesPortalAccount =
  SalesPortalRestaurantListQuery['getSalesPortalAccountsPaginated']['accounts'][number];

export const RESTAURANT_LIST_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// module level options to maintain a stable reference across renders for our internal lazy query
const SEARCH_QUERY_OPTIONS = {
  context: { noBatch: true },
  fetchPolicy: 'network-only' as const,
};

const restaurantListPageTransformer = (
  accounts: SalesPortalAccount[],
): RestaurantListData[] =>
  accounts.flatMap((account) => {
    // skip account without restaurant / supplier relationship
    if (!account.restaurant || !account.supplier) {
      return [];
    }

    return [
      salesPortalRestaurantRowTransformer({
        chatUUID: account.chat_id,
        onHold: account.on_hold,
        restaurant: account.restaurant,
        supplier: account.supplier,
      }),
    ];
  });

// ensure there are no duplicate results in the list (a restaurant could be added while fetching another page)
const dedupeResultsByKey = (
  items: RestaurantListData[],
): RestaurantListData[] => {
  const seen = new Set<string>();

  return items.filter((item) => {
    if (seen.has(item.key)) {
      return false;
    }
    seen.add(item.key);
    return true;
  });
};

/**
 * Loader to fetch and search a paginated list of restaurants.
 *
 * @param businessOrganizationUUID - The supplier org whose restaurants to list.
 *
 * @returns
 *   - `restaurantListData` - rows for the active view (base restaurant list or search results)
 *   - `loading` - initial page load in progress
 *   - `fetchingMore` - a subsequent page is being fetched
 *   - `hasMore` - another page is available for the active view
 *   - `error` - an error encountered when fetching results
 *   - `fetchMore` - load the next page of the active view (safe to call un-awaited)
 *   - `onSearch` - callback to execute a debounced restaurant search
 *   - `onClear` - clear the search and restore the base restaurant list
 *   - `refetch` - refetch the active view from its first page
 *
 * @example
 * const { restaurantListData, loading, fetchingMore, hasMore, fetchMore, onSearch, onClear } =
 *   useSalesPortalRestaurantListLoader(businessOrganizationUUID);
 *
 * // infinite scroll + backend search
 * <RestaurantSelector
 *   listData={restaurantListData}
 *   isLoading={loading}
 *   isFetchingMore={fetchingMore}
 *   onEndReached={hasMore ? fetchMore : undefined}
 *   onSearch={onSearch}
 *   onClear={onClear}
 * />
 */
export const useSalesPortalRestaurantListLoader = (
  businessOrganizationUUID: string,
) => {
  // restuarant list
  const [baseRestaurants, setBaseRestaurants] = useState<RestaurantListData[]>(
    [],
  );
  const [baseCursor, setBaseCursor] = useState<string | null>(null);
  const baseCursorRef = useRef<string | null>(null);

  const {
    error: baseError,
    fetchMore: fetchMoreBase,
    networkStatus,
    refetch: refetchBase,
  } = useGeneratedSalesPortalRestaurantListQuery({
    context: { noBatch: true },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: (completed) => {
      const page = completed.getSalesPortalAccountsPaginated;

      setBaseRestaurants(
        dedupeResultsByKey(restaurantListPageTransformer(page.accounts)),
      );

      setBaseCursor(page.cursor ?? null);
      baseCursorRef.current = page.cursor ?? null;
    },
    variables: {
      businessOrganizationUUID,
      pageSize: RESTAURANT_LIST_PAGE_SIZE,
    },
  });

  // search restaurant list
  const executeSearch =
    useGeneratedSalesPortalRestaurantListLazyQuery(SEARCH_QUERY_OPTIONS);

  // our internal lazy queries do not expose network or error states so we must maintain our own
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [searchRestaurants, setSearchRestaurants] = useState<
    RestaurantListData[]
  >([]);
  const [searchCursor, setSearchCursor] = useState<string | null>(null);
  const searchCursorRef = useRef<string | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFetchingMore, setSearchFetchingMore] = useState(false);
  const [searchError, setSearchError] = useState<Error | undefined>();
  const searchRequestIdRef = useRef(0);

  const isSearching = searchTerm !== null && searchTerm !== '';

  const search = useCallback(
    async (term: string) => {
      const requestId = searchRequestIdRef.current;

      setSearchLoading(true);
      setSearchError(undefined);

      try {
        const { data: searchData } = await executeSearch({
          variables: {
            businessOrganizationUUID,
            pageSize: RESTAURANT_LIST_PAGE_SIZE,
            searchTerm: term,
          },
        });

        if (searchRequestIdRef.current !== requestId) {
          return;
        }

        const page = searchData.getSalesPortalAccountsPaginated;

        setSearchRestaurants(
          dedupeResultsByKey(restaurantListPageTransformer(page.accounts)),
        );

        setSearchCursor(page.cursor ?? null);
        searchCursorRef.current = page.cursor ?? null;
      } catch (caught) {
        if (searchRequestIdRef.current !== requestId) {
          return;
        }

        setSearchError(caught as Error);
      } finally {
        if (searchRequestIdRef.current === requestId) {
          setSearchLoading(false);
        }
      }
    },
    [businessOrganizationUUID, executeSearch],
  );
  const debouncedSearch = useDebouncedCallback(search, SEARCH_DEBOUNCE_MS);

  const onSearch = useCallback(
    (term: string) => {
      searchRequestIdRef.current += 1;
      setSearchTerm(term);

      setSearchCursor(null);
      searchCursorRef.current = null;
      setSearchLoading(true);

      debouncedSearch(term);
    },
    [debouncedSearch],
  );

  const onClear = useCallback(() => {
    debouncedSearch.cancel();
    searchRequestIdRef.current += 1;
    setSearchTerm(null);
    setSearchRestaurants([]);
    setSearchCursor(null);
    searchCursorRef.current = null;
    setSearchLoading(false);
    setSearchError(undefined);
  }, [debouncedSearch]);

  const isLoading = isSearching
    ? searchLoading
    : networkStatus === NetworkStatus.loading;
  const error = isSearching ? searchError : baseError;

  // pagination
  const fetchingRef = useRef(false);
  const fetchMore = useCallback(async () => {
    if (fetchingRef.current || isLoading || error) {
      return;
    }

    // fetch more search results
    if (isSearching) {
      if (searchCursorRef.current === null) {
        return;
      }

      const requestId = searchRequestIdRef.current;
      fetchingRef.current = true;
      setSearchFetchingMore(true);
      try {
        const { data: searchData } = await executeSearch({
          variables: {
            businessOrganizationUUID,
            cursor: searchCursorRef.current,
            pageSize: RESTAURANT_LIST_PAGE_SIZE,
            searchTerm,
          },
        });

        if (searchRequestIdRef.current !== requestId) {
          return;
        }

        const page = searchData.getSalesPortalAccountsPaginated;

        searchCursorRef.current = page.cursor ?? null;
        setSearchCursor(page.cursor ?? null);

        setSearchRestaurants((prev) =>
          dedupeResultsByKey([
            ...prev,
            ...restaurantListPageTransformer(page.accounts),
          ]),
        );
      } catch (caught) {
        if (searchRequestIdRef.current === requestId) {
          setSearchError(caught as Error);
        }
      } finally {
        setSearchFetchingMore(false);
        fetchingRef.current = false;
      }

      return;
    }

    // fetch more base results
    if (baseCursorRef.current === null) {
      return;
    }

    fetchingRef.current = true;
    try {
      const { data: moreData } = await fetchMoreBase({
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }

          return {
            ...prev,
            getSalesPortalAccountsPaginated: {
              ...fetchMoreResult.getSalesPortalAccountsPaginated,
              accounts: [
                ...prev.getSalesPortalAccountsPaginated.accounts,
                ...fetchMoreResult.getSalesPortalAccountsPaginated.accounts,
              ],
            },
          };
        },
        variables: {
          businessOrganizationUUID,
          cursor: baseCursorRef.current,
          pageSize: RESTAURANT_LIST_PAGE_SIZE,
        },
      });
      const page = moreData?.getSalesPortalAccountsPaginated;

      baseCursorRef.current = page?.cursor ?? null;
      setBaseCursor(page?.cursor ?? null);

      setBaseRestaurants((prev) =>
        dedupeResultsByKey([
          ...prev,
          ...restaurantListPageTransformer(page?.accounts ?? []),
        ]),
      );
    } catch {
      // supress load more rejections from `onEndReached`
    } finally {
      fetchingRef.current = false;
    }
  }, [
    businessOrganizationUUID,
    error,
    executeSearch,
    fetchMoreBase,
    isLoading,
    isSearching,
    searchTerm,
  ]);

  const refetch = useCallback(() => {
    if (searchTerm !== null) {
      void search(searchTerm);
      return;
    }

    void refetchBase();
  }, [refetchBase, search, searchTerm]);

  const restaurantListData = isSearching ? searchRestaurants : baseRestaurants;
  const hasMore = isSearching ? searchCursor !== null : baseCursor !== null;
  const fetchingMore = isSearching
    ? searchFetchingMore
    : networkStatus === NetworkStatus.fetchMore;

  return {
    error,
    fetchingMore,
    fetchMore,
    hasMore,
    loading: isLoading,
    onClear,
    onSearch,
    refetch,
    restaurantListData,
  };
};
